import React, { useState, useEffect } from 'react';
import { useSplash } from '../../../contexts/SplashContext';
import TranslatableInput from '../../Common/TranslatableInput';
import TranslatableTextarea from '../../Common/TranslatableTextarea';
import LanguageSwitch from '../../Common/LanguageSwitch';
import CommonSelect from '../../Common/CommonSelect';

import { fetchSettings } from '../../../store/modules/postinggroup/postinggroupActions';
import useFormSettings from '../../../hooks/useFormSettings';

const PostingGroupForm = ({ initialData, onSave, isLoading, onClose }) => {
    const { settings, isLoadingSettings, isSettingsInitialized } = useFormSettings('postinggroup', fetchSettings);
    const { types } = settings || {};
    const splashData = useSplash();
    const languages = splashData?.languages || [];
    const defaultLanguage = languages.find(lang => lang.default)?.code || languages[0]?.code || 'en';
    const [selectedLanguage, setSelectedLanguage] = useState(defaultLanguage);

    const [formData, setFormData] = useState({
        name: {},
        description: {},
        type: null,
        is_active: true,
    });
    
    const [errors, setErrors] = useState({});
    const [isSaving, setIsSaving] = useState(false);

    useEffect(() => {
        if (initialData) {
            setFormData({
                ...initialData,
                name: initialData.name || {},
                description: initialData.description || {},
                type: initialData.type || null,
                is_active: initialData.is_active,
            });
        }
    }, [initialData]);

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));

        if (errors[name]) {
            setErrors(prev => ({...prev, [name]: ''}));
        }
    };

    const handleSelectChange = (name, value) => {
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));

        if (errors[name]) {
            setErrors(prev => ({...prev, [name]: ''}));
        }
    };


    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            setIsSaving(true);
            await onSave(formData);
            setErrors({});
            onClose();
        } catch (error) {
            if (error.errors) {
                const newErrors = {};
                Object.keys(error.errors).forEach(key => {
                    newErrors[key] = Array.isArray(error.errors[key]) 
                        ? error.errors[key][0] 
                        : error.errors[key];
                });
                setErrors(newErrors);
            } else {
                setErrors({
                    name: error.message || 'An error occurred while saving'
                });
            }
            return;
        } finally {
            setIsSaving(false);
        }
    };

    const isSubmitting = isLoading || isSaving;
    const selectedLang = languages.find(l => l.code === selectedLanguage);

    if (isLoadingSettings || !isSettingsInitialized) {
        return (
            <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
                <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading...</span>
                </div>
            </div>
        );
    }

    return (
        <form onSubmit={handleSubmit} className="h-100 d-flex flex-column">
            <div className="d-flex justify-content-end mb-3">
                <LanguageSwitch onLanguageChange={setSelectedLanguage} />
            </div>
            <div className="flex-grow-1">

                <div className="row">
                    <div className="col-md-12">
                        <TranslatableInput
                            label="PostingGroup Name"
                            name="name"
                            value={formData.name}
                            onChange={handleChange}
                            errors={errors}
                            required={true}
                            disabled={isSubmitting}
                            selectedLanguage={selectedLanguage}
                            selectedLang={selectedLang}
                        />
                    </div>
                    <div className="col-md-12">
                        <TranslatableTextarea 
                            label="Description"
                            name="description"
                            rows={1}
                            value={formData.description}
                            onChange={handleChange}
                            errors={errors}
                            disabled={isSubmitting}
                            selectedLanguage={selectedLanguage}
                            selectedLang={selectedLang}
                        />
                    </div>
                    <div className="col-md-12">
                        <CommonSelect
                            name="type"
                            label="Type"
                            value={formData.type}
                            onChange={(value) => handleSelectChange('type', value)}
                            options={types}
                            isSubmitting={isSubmitting}
                            errors={errors}
                            required={true}
                            placeholder="Select type..."
                        />
                    </div>
                </div>
                <div className="row">
                    <div className="col-md-12">
                        <div className="mb-3">
                            <div className="form-check">
                                <input
                                    type="checkbox"
                                    className="form-check-input"
                                    name="is_active"
                                    checked={formData.is_active}
                                    onChange={handleChange}
                                    id="is_active"
                                    disabled={isSubmitting}
                                />
                                <label className="form-check-label user-select-none" htmlFor="is_active">
                                    Active
                                </label>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div className="form-footer">
                <button 
                    type="button" 
                    className="btn btn-light" 
                    onClick={onClose}
                    disabled={isSubmitting}
                >
                    Cancel
                </button>
                <button 
                    type="submit" 
                    className="btn btn-primary" 
                    disabled={isSubmitting}
                >
                    {isSubmitting ? (
                        <>
                            <span className="spinner-border spinner-border-sm me-2" />
                            Saving...
                        </>
                    ) : 'Save'}
                </button>
            </div>
        </form>
    );
};

export default PostingGroupForm; 